// /////////////////////////////////////////////////////////////
// [ファイル] CrmTableProvider.tsx
// [概要] /crm/customers 配下で useCrmTable / useBuildingTable の状態を共有するための Context。
//        親 Layout (CrmCustomerLayout) で一度だけ各フックを呼び、
//        index ルート (一覧) と :id / building/:id ルート (詳細) で同じ状態を参照する。
//        これにより 一覧 → 詳細 → 戻る のフローでデータ・ページ・検索条件・
//        並び順が保持される。建物住所タブの状態 (building) も同居させ、
//        タブ往復でも互いのデータを失わない。
// /////////////////////////////////////////////////////////////
import { createContext, useContext } from 'react';
import type { MutableRefObject } from 'react';
import type { useCrmTable } from '../hooks/useCrmTable';
import type { useBuildingTable } from '../hooks/useBuildingTable';

export type BuildingTableState = ReturnType<typeof useBuildingTable> & {
  // 建物一覧テーブルのスクロール位置 (顧客一覧とは別に保持する)
  scrollPositionRef: MutableRefObject<number>;
};

export type CrmTableContextValue = ReturnType<typeof useCrmTable> & {
  // 一覧テーブルのスクロール位置を Layout 側で保持する。
  // ページがアンマウントされても値が消えないよう ref で保持し、
  // 子ページ (一覧 / 詳細) の再マウント時に復元する。
  scrollPositionRef: MutableRefObject<number>;
  // 建物住所タブの一覧状態一式
  building: BuildingTableState;
  // 現在『建物住所』タブを選択中か (タブ状態の単一の真実は Layout に置く)
  isBuildingTab: boolean;
  setIsBuildingTab: (value: boolean) => void;
};

export const CrmTableContext = createContext<CrmTableContextValue | null>(null);

export const useCrmTableContext = (): CrmTableContextValue => {
  const ctx = useContext(CrmTableContext);
  if (!ctx) {
    throw new Error('useCrmTableContext must be used within <CrmCustomerLayout>');
  }
  return ctx;
};
